import { Checkbox, Modal } from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    CodedMedicine,
    Medicine,
    SubstitutesResponse,
} from '../../../../../api/fetchDrugsSubstitute';
import {
    replaceHEMedication,
    setDrugsSubstitute,
} from '../../../../../store/actions/prescriptionPad';
import { track } from '../../../../../tracking';
import { AntTooltip } from '../../../../common/AntTooltip';
import ReplaceMedicine from './ReplaceMedicine';

export interface SubstituteMedicineObject {
    medicine: Medicine;
    substitutes: CodedMedicine[];
    selectedIndex: number;
    isReplaceable: boolean;
}

export const ReplaceHEMedicinesModal: React.FC<{
    isVisible?: boolean;
    closeMe: () => void;
    drugsSubstitute: SubstitutesResponse;
}> = ({ isVisible, closeMe, drugsSubstitute }) => {
    const [substitutesArray, setSubstitutesArray] = useState<SubstituteMedicineObject[]>([]);
    const [isAcceptChangeChecked, setIsAcceptChangeChecked] = useState<boolean>(true);
    const dispatch = useDispatch();

    useEffect(() => {
        const substitutesArray: SubstituteMedicineObject[] = [];

        if (!drugsSubstitute || !drugsSubstitute.length) return;

        drugsSubstitute.forEach((item) => {
            substitutesArray.push({
                medicine: item[0],
                substitutes: item[1],
                selectedIndex: 0,
                isReplaceable: true,
            });
        });
        setSubstitutesArray(substitutesArray);
    }, [drugsSubstitute]);

    const onSelectChangeHandler = (medIndex: number, optionIndex: number) => {
        const newSubstitutesArray = [...substitutesArray];
        newSubstitutesArray[medIndex].selectedIndex = optionIndex;

        setSubstitutesArray(newSubstitutesArray);

        track('REPLACE_HE_MEDICINES_SELECT_OPTION_CHANGE', {
            currentMed: newSubstitutesArray[medIndex].medicine.name,
            replacedMedId: newSubstitutesArray[medIndex].substitutes[optionIndex].eka_id,
        });
    };

    const onToggle = (index: number) => {
        const newSubstitutesArray = substitutesArray.map((i, ind) =>
            ind === index ? { ...i, isReplaceable: !i.isReplaceable } : i,
        );

        setSubstitutesArray(newSubstitutesArray);

        track('REPLACE_HE_MEDICINES_TOGGLE', {
            currentMed: newSubstitutesArray[index].medicine.name,
            task: newSubstitutesArray[index].isReplaceable ? 'Removed' : 'Added',
        });
    };

    const onAcceptChangeCheckedChange = () => {
        setIsAcceptChangeChecked(!isAcceptChangeChecked);
        track('ACCEPT_CHANGE_CHECKBOX', { action: isAcceptChangeChecked ? 'uncheck' : 'check' });
    };

    const confirmHandler = () => {
        const finalMedicines = substitutesArray.filter((i) => i.isReplaceable);
        const upsertMed = finalMedicines.map((i) => i.substitutes[i.selectedIndex]);
        const deleteMed = finalMedicines.map((i) => i.medicine);
        const substitutes: { [k: string]: CodedMedicine } = {};
        deleteMed.forEach((i, index) => {
            upsertMed[index].index = finalMedicines[index].selectedIndex;
            substitutes[i.eka_id] = upsertMed[index];
        });
        dispatch(replaceHEMedication(substitutes));

        track('CONFIRM_REPLACEMENT', {
            deleteMedNames: JSON.stringify(deleteMed.map((i) => i.name)),
            deleteMedIds: JSON.stringify(deleteMed.map((i) => i.eka_id)),
            upsertMedNames: JSON.stringify(upsertMed.map((i) => i.name)),
            upsertMedIds: JSON.stringify(upsertMed.map((i) => i.eka_id)),
            totalMeds: String(substitutesArray.length),
            replacedMeds: String(finalMedicines.length),
        });
        closeMe();
        const newSubstitutesArray = substitutesArray
            .filter((i) => !i.isReplaceable)
            .map((i) => [i.medicine, i.substitutes]);
        dispatch(setDrugsSubstitute(newSubstitutesArray as SubstitutesResponse));
    };

    const disabled = !isAcceptChangeChecked || substitutesArray.every((i) => !i.isReplaceable);

    return (
        <Modal
            onCancel={closeMe}
            width={800}
            closable={false}
            visible={isVisible}
            destroyOnClose
            footer={null}
            bodyStyle={{
                padding: '0px',
                borderRadius: '12px !important',
                height: '500px',
                display: 'flex',
                flexDirection: 'column',
            }}
            className="overflow-hidden rounded-12"
            style={{ height: '500px' }}
        >
            <div className="h-full w-full flex flex-col bg-darwin-neutral-50 border border-darwin-neutral-200 rounded-12">
                <div className="px-16 py-12 flex items-center justify-between">
                    <div className="flex items-center space-x-8">
                        <div>
                            <svg
                                className="w-18 h-14 fill-darwin-neutral-900"
                                viewBox="0 0 17 13"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M1.75 3.1875V6.25H4.375V3.1875C4.375 2.47656 3.77344 1.875 3.0625 1.875C2.32422 1.875 1.75 2.47656 1.75 3.1875ZM0 3.1875C0 1.51953 1.36719 0.125 3.0625 0.125C4.73047 0.125 6.125 1.51953 6.125 3.1875V3.87109C6.20703 2.96875 6.64453 2.14844 7.41016 1.60156C8.77734 0.589844 10.6914 0.945312 11.6758 2.36719L15.1758 7.48047C16.1602 8.90234 15.832 10.8438 14.4375 11.8281C13.043 12.8125 11.1289 12.4844 10.1719 11.0625L6.64453 5.94922C6.34375 5.48438 6.15234 4.96484 6.125 4.44531V9.3125C6.125 11.0078 4.73047 12.375 3.0625 12.375C1.36719 12.375 0 11.0078 0 9.3125V3.1875ZM8.09375 4.96484L9.87109 7.53516L12.0586 6.00391L10.2266 3.35156C9.81641 2.72266 8.99609 2.61328 8.42188 3.02344C7.82031 3.43359 7.65625 4.30859 8.09375 4.96484ZM13.4258 10.4062L13.4531 10.3789L13.3438 10.4609C13.3711 10.4336 13.3984 10.4336 13.4258 10.4062Z" />
                            </svg>
                        </div>
                        <p className="text-16 text-darwin-neutral-900 font-700">
                            Few medicines will be substituted due to unavailability
                        </p>
                    </div>
                    <div onClick={closeMe}>
                        <svg
                            className="w-14 h-14 fill-darwin-neutral-900 cursor-pointer"
                            viewBox="0 0 12 12"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M10.75 9.71875C11.0312 10.0312 11.0312 10.5 10.75 10.7812C10.4375 11.0938 9.96875 11.0938 9.6875 10.7812L6 7.0625L2.28125 10.7812C1.96875 11.0938 1.5 11.0938 1.21875 10.7812C0.90625 10.5 0.90625 10.0312 1.21875 9.71875L4.9375 6L1.21875 2.28125C0.90625 1.96875 0.90625 1.5 1.21875 1.21875C1.5 0.90625 1.96875 0.90625 2.25 1.21875L6 4.96875L9.71875 1.25C10 0.9375 10.4688 0.9375 10.75 1.25C11.0625 1.53125 11.0625 2 10.75 2.3125L7.03125 6L10.75 9.71875Z" />
                        </svg>
                    </div>
                </div>

                <div className="flex-1 bg-white overflow-scroll border-t border-b border-darwin-neutral-200 p-16">
                    <p className="text-13">
                        Following medicines will be substituted due to their unavailability.{' '}
                    </p>
                    <div className="h-16 bg-white sticky -top-16 z-1"></div>
                    <div className="border-r border-b border-l border-darwin-neutral-100">
                        <div className="px-8 py-5 text-12 border-t border-b border-darwin-neutral-100 grid grid-cols-2 gap-16 bg-darwin-neutral-50 font-700 sticky top-0 z-1">
                            <p className="text-darwin-neutral-900 flex items-center">
                                Can’t be delivered{' '}
                                {substitutesArray.length !== 0 && `(${substitutesArray.length})`}
                            </p>
                            <p className="text-darwin-accent-green-900 bg-darwin-accent-green-100 px-6 py-4 w-max">
                                Substitutes
                            </p>
                        </div>
                        {substitutesArray.map((i, index) => (
                            <ReplaceMedicine
                                key={index}
                                drugSubstitutesResponse={i}
                                isLast={index + 1 === substitutesArray.length}
                                index={index}
                                onSelectChangeHandler={onSelectChangeHandler}
                                onToggle={onToggle}
                            />
                        ))}
                    </div>
                </div>

                <div className="px-16 py-12 flex items-center justify-between">
                    <button
                        className="px-12 py-8 text-darwin-primary-500 font-700"
                        onClick={closeMe}
                    >
                        Cancel
                    </button>
                    <div className="flex items-center space-x-16">
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                onChange={onAcceptChangeCheckedChange}
                                defaultChecked={isAcceptChangeChecked}
                                value={isAcceptChangeChecked}
                                className="text-13 font-400"
                            >
                                I accept the above changes
                            </Checkbox>
                            <AntTooltip content="Kindly understand that Eka offers guidance and support to aid doctors in compliance, but full responsibility for adhering to NMC guidelines rests with the individual doctor.">
                                <div className="pt-3">
                                    <svg
                                        className="w-16 h-16 fill-darwin-neutral-600"
                                        viewBox="0 0 12 13"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path d="M6 12.5C2.67188 12.5 0 9.82812 0 6.5C0 3.19531 2.67188 0.5 6 0.5C9.30469 0.5 12 3.19531 12 6.5C12 9.82812 9.30469 12.5 6 12.5ZM5.0625 8.375C4.73438 8.375 4.5 8.63281 4.5 8.9375C4.5 9.26562 4.73438 9.5 5.0625 9.5H6.9375C7.24219 9.5 7.5 9.26562 7.5 8.9375C7.5 8.63281 7.24219 8.375 6.9375 8.375H6.75V6.3125C6.75 6.00781 6.49219 5.75 6.1875 5.75H5.0625C4.73438 5.75 4.5 6.00781 4.5 6.3125C4.5 6.64062 4.73438 6.875 5.0625 6.875H5.625V8.375H5.0625ZM6 5C6.39844 5 6.75 4.67188 6.75 4.25C6.75 3.85156 6.39844 3.5 6 3.5C5.57812 3.5 5.25 3.85156 5.25 4.25C5.25 4.67188 5.57812 5 6 5Z" />
                                    </svg>
                                </div>
                            </AntTooltip>
                        </div>
                        <div>
                            <button
                                className="px-12 py-8 bg-darwin-primary-500 rounded-4 text-white font-700 disabled:opacity-70 disabled:cursor-not-allowed"
                                disabled={disabled}
                                onClick={confirmHandler}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};
